import { useAnalytics } from '@/hooks/useAnalytics'
import { getErrorMessage } from '@/lib/errors'
import { supabase } from '@/lib/supabase'
import type {
  CreateStoryInput,
  CreateStoryResult,
  IntakeTrigger,
  MinerErrorCode,
  MinerFailure,
  MinerMessage,
  StoryDraft,
} from '@/types/interview'
import { FunctionsHttpError } from '@supabase/supabase-js'
import { useCallback, useEffect, useRef, useState } from 'react'

export type IntakePhase =
  | 'intro'
  | 'starting'
  | 'chatting'
  | 'drafting'
  | 'reviewing'
  | 'done'
  | 'error'

interface StartResponse {
  minerSessionId: string
  message: string
  suggestedCategories: string[]
  turnsRemaining: number
}

interface ReplyResponse {
  message: string
  vignetteComplete: boolean
  turnsRemaining: number
}

interface ExtractResponse {
  draft: StoryDraft | null
  error: 'insufficient_content' | 'non_english' | null
}

interface FinishResponse {
  ok: boolean
  storiesCreated: number
}

/**
 * Mirrors MIN_STUDENT_CHARS_TO_EXTRACT in supabase/functions/story-miner/index.ts. The
 * server refuses to spend a model call drafting from less than this; the button is held
 * back client-side so a student never clicks into a guaranteed rejection.
 */
export const MIN_STUDENT_CHARS_TO_DRAFT = 80

const KNOWN_CODES: MinerErrorCode[] = [
  'MINER_SESSION_LIMIT_REACHED',
  'MINER_TURN_LIMIT_REACHED',
  'MINER_EXTRACT_LIMIT_REACHED',
  'REQUEST_RATE_LIMITED',
  'RATE_LIMIT',
  'MODEL_ERROR',
]

// Fallbacks only — the server sends friendly copy for every code it can return.
const FALLBACK_MESSAGE =
  'We could not reach the story builder. Please check your connection and try again.'

type CallResult<T> = { ok: true; data: T } | { ok: false; failure: MinerFailure }

async function callMiner<T>(body: Record<string, unknown>): Promise<CallResult<T>> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session) {
    return { ok: false, failure: { code: 'ERROR', message: 'Please sign in to continue.' } }
  }

  const { data, error } = await supabase.functions.invoke<T>('story-miner', {
    body,
    headers: { Authorization: `Bearer ${session.access_token}` },
  })

  if (error) {
    // Limit and model errors are typed JSON bodies on a non-2xx response.
    if (error instanceof FunctionsHttpError) {
      try {
        const parsed = (await error.context.json()) as { code?: string; error?: string }
        const code = KNOWN_CODES.find((c) => c === parsed.code) ?? 'ERROR'
        // MODEL_ERROR's global copy says "scoring your answer", which is wrong here — there
        // is no scoring in this conversation — so that one code uses the server's wording.
        const message =
          code === 'MODEL_ERROR' && parsed.error
            ? parsed.error
            : getErrorMessage(code, parsed.error ?? FALLBACK_MESSAGE)
        return { ok: false, failure: { code, message } }
      } catch {
        // fall through
      }
    }
    return { ok: false, failure: { code: 'ERROR', message: FALLBACK_MESSAGE } }
  }
  if (!data) return { ok: false, failure: { code: 'ERROR', message: FALLBACK_MESSAGE } }
  return { ok: true, data }
}

interface UseStoryMinerOptions {
  trigger: IntakeTrigger
  /** useStoryBank().createStory — injected so saving goes through the one gated path. */
  createStory: (input: CreateStoryInput) => Promise<CreateStoryResult>
  /** Current number of stories in the bank, for analytics. */
  storyCount: number
}

export function useStoryMiner({ trigger, createStory, storyCount }: UseStoryMinerOptions) {
  const analytics = useAnalytics()

  const [phase, setPhase] = useState<IntakePhase>('intro')
  const [minerSessionId, setMinerSessionId] = useState<string | null>(null)
  const [messages, setMessages] = useState<MinerMessage[]>([])
  const [suggestedCategories, setSuggestedCategories] = useState<string[]>([])
  const [turnsRemaining, setTurnsRemaining] = useState<number | null>(null)
  const [sending, setSending] = useState(false)
  const [vignetteReady, setVignetteReady] = useState(false)
  const [draft, setDraft] = useState<StoryDraft | null>(null)
  const [draftNotice, setDraftNotice] = useState<'insufficient_content' | 'non_english' | null>(
    null
  )
  const [failure, setFailure] = useState<MinerFailure | null>(null)
  const [savedCount, setSavedCount] = useState(0)
  const [capHit, setCapHit] = useState(false)
  const [saving, setSaving] = useState(false)

  // Messages before this index belong to stories already saved: the drafter takes ONE
  // vignette, so extraction only sees what's been said since the last save.
  const [vignetteStart, setVignetteStart] = useState(0)

  const turnsUsed = messages.filter((m) => m.role === 'interviewer').length
  const studentCharsInVignette = messages
    .slice(vignetteStart)
    .filter((m) => m.role === 'student')
    .reduce((n, m) => n + m.text.trim().length, 0)
  const canDraft = studentCharsInVignette >= MIN_STUDENT_CHARS_TO_DRAFT

  // Latest values for the unmount (abandonment) handler.
  const live = useRef({ phase, savedCount, turnsUsed })
  live.current = { phase, savedCount, turnsUsed }
  const originalDraft = useRef<StoryDraft | null>(null)
  const finishing = useRef(false)

  useEffect(() => {
    return () => {
      const { phase: p, savedCount: saved, turnsUsed: turns } = live.current
      if (p === 'chatting' || p === 'drafting' || p === 'reviewing') {
        analytics.trackIntakeAbandoned({ stories_created: saved, turns_used: turns })
      }
    }
  }, [analytics])

  const start = useCallback(
    async (resumeText?: string) => {
      setPhase('starting')
      setFailure(null)
      const res = await callMiner<StartResponse>({
        action: 'start',
        ...(resumeText ? { resumeText } : {}),
      })
      if (!res.ok) {
        setFailure(res.failure)
        setPhase('error')
        return
      }
      setMinerSessionId(res.data.minerSessionId)
      setMessages([{ role: 'interviewer', text: res.data.message }])
      setSuggestedCategories(res.data.suggestedCategories)
      setTurnsRemaining(res.data.turnsRemaining)
      setPhase('chatting')
      analytics.trackIntakeStarted({ trigger, has_resume: Boolean(resumeText) })
    },
    [analytics, trigger]
  )

  /** Returns true when the message was accepted (the composer clears only then). */
  const send = useCallback(
    async (text: string): Promise<boolean> => {
      if (!minerSessionId || sending) return false
      setSending(true)
      setFailure(null)

      // The server receives the transcript BEFORE this message, plus the message
      // separately — it never has to trust the client's idea of "the latest turn".
      const res = await callMiner<ReplyResponse>({
        action: 'reply',
        minerSessionId,
        transcript: messages,
        studentMessage: text,
      })
      setSending(false)

      if (!res.ok) {
        if (res.failure.code === 'MINER_TURN_LIMIT_REACHED') setTurnsRemaining(0)
        setFailure(res.failure)
        return false
      }

      setMessages((prev) => [
        ...prev,
        { role: 'student', text },
        { role: 'interviewer', text: res.data.message },
      ])
      setTurnsRemaining(res.data.turnsRemaining)
      setVignetteReady(res.data.vignetteComplete)
      return true
    },
    [minerSessionId, sending, messages]
  )

  const requestDraft = useCallback(async () => {
    if (!minerSessionId || !canDraft) return
    setPhase('drafting')
    setFailure(null)
    setDraftNotice(null)

    const res = await callMiner<ExtractResponse>({
      action: 'extract',
      minerSessionId,
      transcript: messages.slice(vignetteStart),
    })

    if (!res.ok) {
      setFailure(res.failure)
      setPhase('chatting')
      return
    }
    if (!res.data.draft) {
      // Not enough said yet — keep talking, the server gave the attempt back.
      setDraftNotice(res.data.error)
      setPhase('chatting')
      return
    }

    originalDraft.current = res.data.draft
    setDraft(res.data.draft)
    setPhase('reviewing')
    analytics.trackIntakeStoryDrafted({
      competency: res.data.draft.competency ?? 'unknown',
      missing_components_count: res.data.draft.missingComponents.length,
    })
  }, [minerSessionId, canDraft, messages, vignetteStart, analytics])

  /** Saves the student's (possibly edited) draft. Returns true on success. */
  const saveDraft = useCallback(
    async (edited: StoryDraft): Promise<boolean> => {
      if (!edited.competency || saving) return false
      setSaving(true)

      const result = await createStory({
        source: 'mined',
        title: edited.title.trim(),
        competency: edited.competency,
        star: {
          situation: edited.situation.trim(),
          ...(edited.task?.trim() && { task: edited.task.trim() }),
          ...(edited.action?.trim() && { action: edited.action.trim() }),
          ...(edited.result?.trim() && { result: edited.result.trim() }),
        },
        ...(edited.roleTag?.trim() && { roleTag: edited.roleTag.trim() }),
      })
      setSaving(false)

      if (result.ok) {
        const original = originalDraft.current
        analytics.trackIntakeStorySaved({
          competency: edited.competency,
          story_count: result.storyCount,
          was_edited:
            !original ||
            original.title !== edited.title ||
            original.situation !== edited.situation ||
            original.task !== edited.task ||
            original.action !== edited.action ||
            original.result !== edited.result ||
            original.competency !== edited.competency,
        })
        setSavedCount((n) => n + 1)
        setVignetteStart(messages.length)
        setVignetteReady(false)
        setDraft(null)
        setPhase('chatting')
        return true
      }

      if (result.code === 'STORY_CAP_REACHED') {
        setCapHit(true)
        analytics.trackIntakeCapHit({ story_count: result.storyCount })
      } else {
        setFailure({
          code: 'ERROR',
          message: 'We could not save your story. Please try again.',
        })
      }
      return false
    },
    [createStory, saving, analytics, messages.length]
  )

  /** Back to the conversation without saving; the draft is discarded. */
  const keepTalking = useCallback(() => {
    setDraft(null)
    setCapHit(false)
    setPhase('chatting')
  }, [])

  const finish = useCallback(async () => {
    // Leave the abandonment-armed phases before awaiting: a double-click or navigating
    // away mid-request must not emit a second completion or an abandonment.
    if (finishing.current) return
    finishing.current = true
    setPhase('done')
    live.current = { ...live.current, phase: 'done' }
    if (minerSessionId) {
      const res = await callMiner<FinishResponse>({ action: 'finish', minerSessionId })
      // Best-effort bookkeeping — a failure here must never trap the student.
      const created = res.ok ? res.data.storiesCreated : savedCount
      analytics.trackIntakeCompleted({ stories_created: created, turns_used: turnsUsed })
    }
  }, [minerSessionId, savedCount, turnsUsed, analytics])

  const reset = useCallback(() => {
    finishing.current = false
    setFailure(null)
    setPhase('intro')
  }, [])

  return {
    phase,
    messages,
    suggestedCategories,
    turnsRemaining,
    turnsUsed,
    sending,
    vignetteReady,
    canDraft,
    draft,
    draftNotice,
    failure,
    savedCount,
    capHit,
    saving,
    storyCount,
    start,
    send,
    requestDraft,
    saveDraft,
    keepTalking,
    finish,
    reset,
  }
}
